import { validateSalePayment } from "../../utils/SalePaymentValidation";
import TransactionDetails from "../../components/TransactionDetails";
import EntityLabel from "../../components/EntityLabel";
import { formatDateTime, localDateTimeInput } from "../../utils/DateTime";
import { saleBalance, netSaleTotal, saleReturnCredit } from "../../utils/SaleTotals";
import ReturnDialog from "../../components/Returns/ReturnDialog";
import { useAuth } from "../../auth/useAuth";
import { useState } from "react";
import ReceiptPreview from "../../components/Receipt/ReceiptPreview";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import ManagementPageHTML, {
  IdentityCell,
  StatusBadge,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  ManagementColumn,
  ManagementDialogField,
} from "../../components/ManagementPage/ManagementPageTypes";
import type { Sale } from "../../models/Sale";
import type { SaleItem } from "../../models/Sale";
import LineItemEditor, {
  CalculateLineItemTotals,
  ValidateLineItems,
} from "../../components/LineItemEditor/LineItemEditor";
import { SaleService } from "../../services/SaleService";
import { useSale } from "./Sale";
import "./SaleCss.css";

const money = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
});
const columns: ManagementColumn<Sale>[] = [
  {
    Key: "Sale",
    Header: "Sale",
    Render: (row) => (
      <IdentityCell
        Icon="fa-receipt"
        Title={row.SaleNumber || row.Code}
        Subtitle={formatDateTime(row.SoldAt)}
      />
    ),
  },
  {
    Key: "Customer",
    Header: "Customer",
    Render: (row) => <EntityLabel entity="customer" code={row.CustomerCode} fallback="Walk-in" />,
  },
  {
    Key: "Order",
    Header: "Order",
    Render: (row) => row.OrderCode || "Direct sale",
  },

  {
    Key: "Amount",
    Header: "Total",
    Render: (row) => <strong>{money.format(row.TotalAmount)}</strong>,
  },
  {
    Key: "ReturnCredit", Header: "Approved return value", Render: row=>money.format(saleReturnCredit(row)),
  },
  {
    Key: "Balance",
    Header: "Balance",
    Render: (row) =>
      money.format(saleBalance(row)),
  },
  {
    Key: "Status",
    Header: "Payment",
    Render: (row) => <StatusBadge Status={row.PaymentStatus} />,
  },
];

export default function SaleHTML() {
  const { hasPermission, state: authState } = useAuth();
  const canPrint =
    hasPermission("Sale.PrintReceipt") ||
    (authState.RoleUis === undefined && hasPermission("Sale.Manage"));
  const { Warehouse } = useWarehouse();
  const [receiptSale, setReceiptSale] = useState<Sale>();
  const [detailsCode,setDetailsCode]=useState("");
  const [returnCode, setReturnCode] = useState("");
  const [revision, setRevision] = useState(0);
  const { Customers, Orders, Products, Error } = useSale(Warehouse?.Code);
  const fields: ManagementDialogField<Sale>[] = [
    { Key: "Code", Label: "Sale code", Disable: true },
    {
      Key: "CustomerCode",
      Label: "Customer",
      Type: "select",
      Options: Customers.map((customer) => ({
        display: String(customer.Value),
        value: customer.Key,
        other: customer,
      })),
    },
    {
      Key: "OrderCode",
      Label: "Order (optional)",
      Type: "select",
      Options: Orders.map((order) => ({
        display: `${order.OrderNumber || order.Code} — ${order.CustomerCode}`,
        value: order.Code,
        other: order,
      })),
    },

    { Key: "DiscountAmount", Label: "Discount", Type: "money" },
    // {
    //   Key: "TotalAmount",
    //   Label: "Total amount",
    //   Type: "money",
    //   Required: true,
    // },
    {
      Key: "PaymentType",
      Label: "Payment type",
      Type: "select",
      Options: ["Immediate", "Credit"],
      Required: true,
    },
    {
      Key: "PaymentStatus",
      Label: "Payment status",
      Type: "select",
      Options: ["Pending", "PartiallyPaid", "Paid", "Failed", "Refunded"],
      Required: true,
    },
    { Key: "AmountPaid", Label: "Amount paid", Type: "money", Required: true },
    // { Key: "BalanceDue", Label: "Balance due", Type: "money" },
    { Key: "CreditDays", VisibleWhen: v=>v.PaymentType === "Credit", Label: "Credit days", Type: "integer" },
    {
      VisibleWhen: v=>v.PaymentType === "Credit",
      Key: "PaymentDueAt",
      Label: "Payment due",
      Type: "datetime",
    },
    {
      Key: "SoldAt",
      Label: "Sold at",
      Type: "datetime",
      Required: true,
    },
  ];
  return (
    <>
      {Error && <p role="alert">{Error}</p>}
      <ManagementPageHTML key={`${Warehouse?.Code}:${revision}`}
        Service={SaleService(Warehouse?.Code)}
        OnSaved={(rows, mode) => {
          if (mode === "Create" && rows[0]?.Code && canPrint)
            setReceiptSale(rows[0]);
        }}
        ActiveRowKey={detailsCode || returnCode} RenderRowActions={(row) => <>
          <button type="button" className="management-action" aria-label="View sale details" onClick={()=>setDetailsCode(row.Code)}><i className="fa-solid fa-eye"/></button>
          {row.IsCompleted && (hasPermission("Sale.Return") || hasPermission("Sale.ApproveReturn")) && <button type="button" className="management-action" aria-label="Manage sale returns" onClick={()=>setReturnCode(row.Code)}><i className="fa-solid fa-rotate-left" /></button>}
          {canPrint ? (
            <button
              type="button"
              className="management-action"
              aria-label="Print receipt"
              onClick={() => setReceiptSale(row)}
            >
              <i className="fa-solid fa-print" />
            </button>
          ) : null}</>
        }
        CanEditRow={row=>row.IsCompleted === false}
        CanDeleteRow={row=>row.IsCompleted === false}
        DefaultValues={{
          WareHouse: Warehouse?.Code,
          Items: [],
          CurrencyCode: "NGN",
          PaymentType: "Immediate",
          PaymentStatus: "Paid",
          SoldAt: localDateTimeInput(new Date().toISOString()),
          PaymentDueAt: localDateTimeInput(new Date().toISOString()),
        }}
        OnFormValueChange={(field, value, values, context) => {
          if (field === "OrderCode") {
            const order = Orders.find((item) => item.Code === value);
            if (!order) return;
            return {
              CustomerCode: order.CustomerCode,
              Items: order.Items,
              DiscountAmount: order.DiscountAmount,
              TotalAmount: order.TotalAmount,
              CurrencyCode: order.CurrencyCode,
              PaymentStatus: order.PaymentStatus,
              ...(context.DialogMode === "Create" && !context.ChangedFields.has("AmountPaid") ? {AmountPaid:order.TotalAmount} : {}),
              BalanceDue: context.DialogMode === "Create" && !context.ChangedFields.has("AmountPaid") ? 0 : Math.max(0,order.TotalAmount-Number(values.AmountPaid || 0)),
            };
          }
          if (field === "Items" || field === "DiscountAmount") {
            const items = (
              field === "Items" ? value : values.Items
            ) as SaleItem[];
            const discount = Number(
              field === "DiscountAmount" ? value : values.DiscountAmount || 0,
            );
            const totals = CalculateLineItemTotals(items ?? [], discount);
            const amountPaid = context.DialogMode === "Create" && !context.ChangedFields.has("AmountPaid")
              ? totals.TotalAmount : Number(values.AmountPaid || 0);
            return {
              ...(context.DialogMode === "Create" && !context.ChangedFields.has("AmountPaid") ? {AmountPaid:amountPaid} : {}),
              TotalAmount: totals.TotalAmount,
              BalanceDue: Math.max(
                0,
                totals.TotalAmount - amountPaid,
              ),
            };
          }
          if (field === "AmountPaid") {
            return {
              BalanceDue: Math.max(
                0,
                Number(values.TotalAmount || 0) - Number(value || 0),
              ),
            };
          }
        }}
        ValidateForm={(values) => {
          const errors: Record<string, string> = {};
          if (values.PaymentType === "Credit" && !values.PaymentDueAt)
            errors.PaymentDueAt = "Payment due is required for credit sales.";
          const discount=Number(values.DiscountAmount||0);
          const items=(values.Items??[]) as SaleItem[];
          if(!Number.isFinite(discount)||discount<0||discount>items.reduce((s,l)=>s+l.Quantity*l.UnitPrice,0))errors.DiscountAmount="Discount must be between zero and the subtotal.";
          if(discount>0&&items.some(l=>Number(l.DiscountAmount||0)>0))errors.DiscountAmount="Apply discounts to the sale or its lines, not both.";
          const paymentError = validateSalePayment(values.PaymentStatus, values.AmountPaid, CalculateLineItemTotals(items, discount).TotalAmount);
          if (paymentError) errors.AmountPaid = paymentError;
          const error = ValidateLineItems(values.Items, Products);
          if (error) errors.Items = error;
          return errors;
        }}
        RenderDialogContent={({
          FormValues,
          SetFormValue,
          ValidationErrors,
        }) => (
          <LineItemEditor<SaleItem>
            HideSummary
            Items={(FormValues.Items as SaleItem[]) ?? []}
            Products={Products}
            DiscountAmount={Number(FormValues.DiscountAmount || 0)}
            AmountPaid={Number(FormValues.AmountPaid || 0)}
            CurrencyCode={String(FormValues.CurrencyCode || "NGN")}
            OnChange={(items) => SetFormValue("Items", items)}
            Error={ValidationErrors.Items}
          />
        )}
        DialogSaveLabel={canPrint ? "Create sale & preview receipt" : "Create sale"}
        DialogFooter={v=><><small>Total</small><strong>{money.format(CalculateLineItemTotals((v.Items??[]) as SaleItem[],Number(v.DiscountAmount||0)).TotalAmount)}</strong></>}
        DialogSummary={v=>{const totals=CalculateLineItemTotals((v.Items ?? []) as SaleItem[], Number(v.DiscountAmount||0));const paid=Number(v.AmountPaid||0);return <div className="editor-summary"><div><span>Subtotal</span><strong>{money.format(totals.SubTotal)}</strong></div><div><span>Discount</span><strong>{money.format(Number(v.DiscountAmount||0))}</strong></div><div className="editor-summary-total"><span>Total</span><strong>{money.format(totals.TotalAmount)}</strong></div><div><span>Balance due</span><strong>{money.format(Math.max(0,totals.TotalAmount-paid))}</strong></div><div><span>Change</span><strong>{money.format(Math.max(0,paid-totals.TotalAmount))}</strong></div></div>;}}
        DialogSections={[
          {Title:"Sale details", Keys:["Code","CustomerCode","OrderCode","SoldAt"]},
          {Title:"Payment and summary", Keys:["PaymentType","PaymentStatus","DiscountAmount","AmountPaid","CreditDays","PaymentDueAt"], Side:true},
        ]}
        Title="Sales"
        Eyebrow="Revenue management"
        Description="Create direct sales or convert an existing order into a sale."
        AddLabel="Record sale"
        SearchPlaceholder="Search sales, orders or customers"
        Rows={[]}
        Columns={columns}
        DialogFields={fields}
        Statuses={["Pending", "PartiallyPaid", "Paid", "Failed", "Refunded"]}
        GetRowKey={(row) => row.Code}
        GetSearchText={(row) =>
          `${row.Code} ${row.SaleNumber} ${row.OrderCode ?? ""} ${row.CustomerCode ?? ""}`
        }
        GetStatus={(row) => row.PaymentStatus}
        GetSummaries={(rows, total) => [
          { Label: "Sales", Value: total, Icon: "fa-receipt", Tone: "Blue" },
          {
            Label: "Net sales on this page",
            Value: money.format(
              rows.reduce((sum, x) => sum + netSaleTotal(x), 0),
            ),
            Icon: "fa-naira-sign",
            Tone: "Green",
          },
          {
            Label: "Paid on this page",
            Value: money.format(rows.reduce((sum, x) => sum + Math.min(Math.max(0,x.AmountPaid),netSaleTotal(x)), 0)),
            Icon: "fa-circle-check",
            Tone: "Purple",
          },
          {
            Label: "Outstanding on this page",
            Value: money.format(
              rows.reduce(
                (sum, x) => sum + saleBalance(x),
                0,
              ),
            ),
            Icon: "fa-clock",
            Tone: "Orange",
          },
        ]}
        Summaries={[]}
        Filters={[
          {
            Key: "PaymentType",
            Label: "Payment type",
            Options: ["Immediate", "Credit"],
            GetValue: (row) => row.PaymentType,
          },
        ]}
      />
      {detailsCode && <TransactionDetails kind="sale" code={detailsCode} onClose={()=>setDetailsCode("")}/>}
      {returnCode && <ReturnDialog key={Warehouse?.Code} kind="sale" code={returnCode} onClose={()=>setReturnCode("")} onChanged={()=>setRevision(x=>x+1)} />}
      {receiptSale && (
        <ReceiptPreview
          sale={receiptSale}
          onClose={() => setReceiptSale(undefined)}
        />
      )}
    </>
  );
}
